import type { components } from '@/api/schema';
import { shortDate } from '@/components/ui';
import { ACTION_LABEL, ACTION_ORDER } from './permissionWords';

type AccessLine = components['schemas']['AccessLineDto'];

/**
 * What someone can do, grouped by module, one plain line per thing —
 * "Items: See, Add, Change" — each with why: which role gives it, or an extra
 * someone gave them with their reason. No permission codes (.docs/019).
 */
export function AccessLines({ lines, emptyText }: { lines: AccessLine[]; emptyText?: string }) {
  const held = lines.filter((l) => l.effective);
  const removed = lines.filter((l) => !l.effective && l.override === 'revoke');

  if (held.length === 0 && removed.length === 0) {
    return <p className="muted">{emptyText ?? 'Nothing yet.'}</p>;
  }

  const modules = new Map<string, { label: string; resources: Map<string, AccessLine[]> }>();
  for (const line of held) {
    const mod = modules.get(line.module) ?? { label: line.moduleLabel, resources: new Map() };
    const list = mod.resources.get(line.resource) ?? [];
    list.push(line);
    mod.resources.set(line.resource, list);
    modules.set(line.module, mod);
  }

  return (
    <div className="accesslines">
      {[...modules.entries()].map(([key, mod]) => (
        <section key={key} className="accesslines__module">
          <h4>{mod.label}</h4>
          <ul>
            {[...mod.resources.values()].map((actions) => {
              const sorted = [...actions].sort(
                (a, b) => ACTION_ORDER.indexOf(a.action) - ACTION_ORDER.indexOf(b.action),
              );
              const first = sorted[0]!;
              const extras = sorted.filter((a) => a.override === 'grant');
              const roles = [...new Set(sorted.flatMap((a) => a.fromRoles))];
              return (
                <li key={first.resource}>
                  <span className="accesslines__what">
                    {first.resourceLabel}: {sorted.map((a) => ACTION_LABEL[a.action] ?? a.action).join(', ')}
                  </span>
                  <span className="accesslines__why">
                    {roles.length > 0 && <>from {roles.join(', ')}</>}
                    {extras.map((e) => (
                      <span key={e.code} className="pill">
                        + {ACTION_LABEL[e.action] ?? e.action} given
                        {e.by ? ` by ${e.by}` : ''}
                        {e.at ? ` on ${shortDate(e.at)}` : ''}
                        {e.reason ? `: ${e.reason}` : ''}
                        {e.expiresAt ? ` (until ${shortDate(e.expiresAt)})` : ''}
                      </span>
                    ))}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      {removed.length > 0 && (
        <section className="accesslines__module">
          <h4>Taken away from them</h4>
          <ul>
            {removed.map((r) => (
              <li key={r.code}>
                <span className="accesslines__what">
                  {r.moduleLabel} › {r.resourceLabel}: {ACTION_LABEL[r.action] ?? r.action}
                </span>
                <span className="accesslines__why">
                  {r.by ? `by ${r.by}` : ''}
                  {r.reason ? `: ${r.reason}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
